import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";

// Roboto — TB PE's own font, self-hosted via @fontsource so the SPA never
// depends on Google Fonts at runtime (the TB iframe sandbox and some tenant
// CSPs block third-party font CDNs outright).
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
// JetBrains Mono — paired with Roboto for everything numeric (axis ticks,
// counters, deltas). Self-hosted same as Roboto.
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/jetbrains-mono/700.css";

import App from "./App";
import "./styles.css";

// Stale-chunk recovery (load-bearing for the embedded dashboard). Routes are
// code-split (App.tsx React.lazy + the echarts manualChunk), so a TB iframe
// left open for days will, after a redeploy, request a route chunk whose
// hashed filename no longer exists on the next tab switch. Vite emits
// `vite:preloadError` for exactly that; a single reload pulls the fresh index
// + chunk graph and self-heals. Without this the stale tab throws an unhandled
// rejection — React error boundaries do NOT catch a failed dynamic import — and
// the iframe blanks. A bare reload keeps the URL verbatim, so the boot
// ?live/?mock/?token/?theme/?tb params (carryParams ENV_KEYS) all survive.
// Registered at module top so it is armed before any lazy chunk is requested.
//
// Accepted limitation: the reload discards transient in-progress UI state (a
// half-typed alarm note, an unsaved schedule draft). This only fires on the
// first chunk request after a redeploy, and on the unattended 24/7 wall
// display nobody is mid-edit — a one-time reload beats a blank iframe. A
// silent reload (no banner) is also right here: an alarming "Reloading…"
// flash on an unattended display would be worse than the sub-second reload.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  // Loop guard with a TTL, not a permanent flag: a reload that immediately
  // re-fails (chunk genuinely unreachable — offline / server down) is
  // suppressed, but a SECOND independent stale-chunk failure later in the same
  // long-lived session (a second redeploy) can still recover.
  const KEY = "ems-chunk-reloaded-at";
  const LOOP_WINDOW_MS = 20_000;
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (last && Date.now() - last < LOOP_WINDOW_MS) return; // just reloaded → don't loop
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    /* storage disabled (sandboxed iframe) — fall through to a single reload */
  }
  window.location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: {
    // NB: these are DEFAULTS — TanStack v5 merges per-query options over
    // them, so individual queries may opt back in. The shared alarms query
    // (src/hooks/useAlarms.ts) deliberately overrides
    // `refetchOnWindowFocus` (true) and adds `refetchIntervalInBackground`
    // so a backgrounded tab keeps receiving alarm state (XTRA-02).
    queries: { staleTime: 5_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
